import { motion } from "framer-motion";
import {
  Phone, Shield,
  Network,
  CreditCard,
  Router,
  PackageCheck,
  Wrench,
  Globe,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const usps = [
  {
    icon: Phone,
    title: "Echte hulp van echte mensen",
    text: "Bij ons krijg je geen medewerker die een standaard telefoonscript opdreunt. Je belt, je hebt direct iemand met verstand van zaken, en we helpen je snel.",
  },
  {
    icon: Network,
    title: "Wij beheren jouw hele netwerk",
    text: "Internet is leuk, maar het moet doorwerken naar je apparaten. Problemen met WiFi? Kapotte switch? Vragen over je bekabeling? Bel ons. Klaar.",
  },
  {
    icon: CreditCard,
    title: "Altijd een vaste prijs mét netwerkbeheer",
    text: "Bij Rinqel zit netwerkbeheer altijd inbegrepen. Geen verrassingen. Geen verborgen kosten. Gewoon duidelijk.",
  },
  {
    icon: Router,
    title: "Wij leveren onze eigen routers mee",
    text: "Wij plaatsen onze eigen router zodat wij maximale controle hebben en jij minimale storingen hebt. Als er wél iets misgaat: wij kunnen meteen meekijken en oplossen.",
  },
  {
    icon: PackageCheck,
    title: "Alles kant-en-klaar opgeleverd",
    text: "Geen dozen, geen kabelwirwar, geen puzzelboekjes. Wij installeren de verbinding zó dat jij er direct mee kunt werken.",
  },
  {
    icon: Wrench,
    title: "Infrastructuur vernieuwen? Doen we ook",
    text: "Is je bekabeling oud? Zijn je switches uit 2009? We kunnen deze vervangen zodat alles storingsvrij en supersnel loopt.",
  },
  {
    icon: Globe,
    title: "Altijd een vast IP-adres",
    text: "Lekker handig voor slimme apparaten, thuiswerken, servers of domotica. Uitzondering: Starlink — maar zelfs dan lossen we het netjes op met Dynamic DNS.",
  },
  {
    icon: Shield,
    title: "Meegroeiend met je bedrijf",
    text: "Begin klein en schaal op wanneer het nodig is. Snelheid upgraden, een extra lijn erbij of een backup-verbinding toevoegen? Geregeld. Zonder gedoe.",
  },
];

const InternetContent = () => {
  return (
    <>
      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Internet: het ding waar alles op draait
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Zonder internet kun je tegenwoordig ongeveer net zo veel als een auto
              zonder wielen. Daarom is een goede, stabiele internetaansluiting
              superbelangrijk — thuis, op kantoor, of in je bedrijf. En dat is
              precies waar <span className="font-semibold text-foreground">Rinqel</span> binnenkomt.
              Wij regelen niet alleen je internet, maar eigenlijk alles wat er omheen hangt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* USPs */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Waarom internet van Rinqel{" "}
            <span className="text-gradient">zo lekker werkt</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {usps.map((usp, i) => {
              const Icon = usp.icon;
              return (
                <motion.div
                  key={usp.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-hero-gradient text-primary-foreground">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{usp.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {usp.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Netwerken link */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <motion.div
            className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold md:text-2xl">
              Meer weten over netwerken?
            </h3>
            <p className="text-muted-foreground">
              Over kabels, WiFi, toegangspunten, roaming, of hoe je voorkomt dat
              je buurman op je WiFi zit?
            </p>
            <Button variant="hero" size="lg" asChild>
              <Link to="/diensten/netwerken">
                Bekijk netwerken
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default InternetContent;
